import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { Pagination, Spinner } from '@consigli/facade';
import { useGetBlobReadUrlQuery, useGetFileReadUrlQuery } from '@consigli/hooks';
import { Blob, File as ConsigliFile } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { FC, useCallback, useMemo } from 'react';

import { DataTable, ObjectDetailView, createBackofficeRenderers } from '@/components';
import { isValidDocumentType, resolveViewerFileType } from '@/utils/document-check';
import { ServiceType } from '@/utils/types';

import { DocumentsProvider, useDocumentsContext } from './context/documents-context';
import { DeleteModalWithContext, EditFilenameModalWithContext } from './table/modals';
import { useTableConfig } from './table/table-config';

interface DocumentsTabProps {
  serviceId: string;
  projectId: number;
  serviceType: ServiceType;
  isBidReview?: boolean;
}

const LoadingOverlay: FC<{ onClose: () => void }> = ({ onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    onClick={onClose}
    role="presentation"
  >
    <Spinner />
  </div>
);

const BlobDocumentViewer: FC<{
  projectId: number;
  packageId: string;
  blob: Blob;
  onClose: () => void;
}> = ({ projectId, packageId, blob, onClose }) => {
  const { data } = useGetBlobReadUrlQuery({ projectId, packageId, blobId: blob.id });
  const renderers = useMemo(() => createBackofficeRenderers(), []);

  if (!data?.readUrl) {
    return <LoadingOverlay onClose={onClose} />;
  }

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={data.readUrl}
      title={blob.name}
      fileName={blob.name}
      fileType={resolveViewerFileType(blob.name)}
      showSidebar={false}
      renderers={renderers}
    />
  );
};

const FileDocumentViewer: FC<{
  projectId: number;
  file: ConsigliFile;
  onClose: () => void;
}> = ({ projectId, file, onClose }) => {
  const { data } = useGetFileReadUrlQuery({ projectId, fileId: file.id });
  const renderers = useMemo(() => createBackofficeRenderers(), []);

  if (!data?.readUrl) {
    return <LoadingOverlay onClose={onClose} />;
  }

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
      src={data.readUrl}
      title={file.filename}
      fileName={file.filename}
      fileType={resolveViewerFileType(file.filename)}
      showSidebar={false}
      renderers={renderers}
    />
  );
};

const DocumentsTabContent = () => {
  const {
    results,
    isLoading,
    isFetching,
    selectedItem,
    isDetailViewOpen,
    setIsDetailViewOpen,
    setSelectedItem,
    previewItem,
    setPreviewItem,
    projectId,
    serviceId,
    serviceType,
    isBidReview,
    page,
    setPage,
    totalCount,
    pageSize,
  } = useDocumentsContext();

  const columns = useTableConfig({ isBidReview });

  const usesBlobViewer = serviceType === ServiceType.DOCUMENT_PACKAGE;

  const handleRowClick = useCallback(
    (row: unknown) => {
      const item = row as Blob | ConsigliFile;
      const filename = 'name' in item ? (item as Blob).name : (item as ConsigliFile).filename;
      if (isValidDocumentType(filename)) {
        setPreviewItem(item);
      }
    },
    [setPreviewItem],
  );

  const previewItemId = previewItem ? (previewItem as { id: string }).id : null;

  const rowClassName = useCallback(
    (row: unknown) => {
      const item = row as { id: string };
      return item.id === previewItemId ? 'bg-blue-50/60' : undefined;
    },
    [previewItemId],
  );

  return (
    <>
      <div className="w-full">
        {!isLoading && <p className="text-sm text-gray-500 mb-2">{totalCount} files</p>}
        <DataTable<unknown, unknown>
          columns={columns as ColumnDef<unknown, unknown>[]}
          data={results}
          isLoading={isLoading || isFetching}
          scrollable
          height="60vh"
          onRowClick={handleRowClick}
          rowClassName={rowClassName}
        />
        {totalCount > pageSize && (
          <div className="flex justify-center pt-4">
            <Pagination
              currentPage={page}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      <DeleteModalWithContext />

      {selectedItem && (
        <ObjectDetailView
          object={selectedItem}
          objectType="Document"
          summary={`Document "${'name' in selectedItem ? selectedItem.name : selectedItem.filename}" details including type, status, and metadata.`}
          isOpen={isDetailViewOpen}
          onOpenChange={(open: boolean) => {
            setIsDetailViewOpen(open);
            if (!open) {
              setSelectedItem(null);
            }
          }}
        />
      )}

      <EditFilenameModalWithContext />

      {previewItem && usesBlobViewer && (
        <BlobDocumentViewer
          projectId={projectId}
          packageId={serviceId}
          blob={previewItem as Blob}
          onClose={() => setPreviewItem(null)}
        />
      )}

      {previewItem && !usesBlobViewer && (
        <FileDocumentViewer
          projectId={projectId}
          file={previewItem as ConsigliFile}
          onClose={() => setPreviewItem(null)}
        />
      )}
    </>
  );
};

export const DocumentsTab = ({
  serviceId,
  projectId,
  serviceType,
  isBidReview = false,
}: DocumentsTabProps) => {
  return (
    <DocumentsProvider
      serviceId={serviceId}
      projectId={projectId}
      serviceType={serviceType}
      isBidReview={isBidReview}
    >
      <DocumentsTabContent />
    </DocumentsProvider>
  );
};
